import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useContext,
  useState,
} from "react";
import { Focusable } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Heading } from "@/components/ui/heading";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";

type TestPreviewCOntextValue = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const TestPreviewContext = createContext<TestPreviewCOntextValue>(
  {} as TestPreviewCOntextValue,
);

const testTypeLabelMap = {
  SEROLOGY: "Serology",
  MICROBIOLOGY: "Micro",
  CLINICAL: "Clinical",
};

export function TestPreviewProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <TestPreviewContext.Provider value={{ open, setOpen }}>
      {children}
    </TestPreviewContext.Provider>
  );
}

export function useTestOrderPreview() {
  return useContext(TestPreviewContext);
}

export function TestPreviewHoverCard({
  accession,
  className,
  children,
}: {
  accession: { id: number; testType: string | null };
  className?: string;
  children?: React.ReactElement;
}) {
  const { open, setOpen } = useContext(TestPreviewContext);
  const { data, isLoading } = trpc.accession.testOrders.useQuery(accession.id, {
    enabled: open,
    staleTime: 1000 * 60,
  });
  const orders = data || [];
  const truncated = orders.filter((o) => !o.isReflex).slice(0, 10);

  const moreText =
    orders.length > 10 ? (
      <li className="text-muted-foreground italic">
        {orders.length - 10} more
      </li>
    ) : null;

  if (!accession.testType) return null;

  return (
    <TooltipTrigger
      delay={100}
      closeDelay={100}
      isOpen={open}
      onOpenChange={setOpen}
    >
      {children ? (
        children
      ) : (
        <Focusable>
          <Badge
            className={cn("cursor-default", className)}
            role="button"
            size="sm"
          >
            {testTypeLabelMap[accession.testType] ?? accession.testType}
          </Badge>
        </Focusable>
      )}
      <Tooltip
        placement="right top"
        className="max-w-56 w-56 bg-background border text-primary shadow-md overflow-hidden text-xs/tight border-border overflow-y-auto p-0"
      >
        <Heading displayLevel={3} className="px-2 py-1">
          Test orders
        </Heading>
        <Separator />
        <ul className="list-inside py-1 px-2 space-y-0.5">
          {isLoading
            ? new Array(3).fill(0).map((_, i) => (
                <li key={i}>
                  <Skeleton className="h-2 w-20 inline-block" />
                </li>
              ))
            : truncated.map((order) => (
                <li key={order.id} className="min-w-0 truncate">
                  {order.testSpec.name}
                </li>
              ))}
          {moreText}
        </ul>
      </Tooltip>
    </TooltipTrigger>
  );

  // return (
  //   <HoverCard
  //     isOpen={open}
  //     onOpenChange={setOpen}
  //     delay={100}
  //     placement="right top"
  //   >
  //     <HoverCardTrigger>
  //       {children ? (
  //         children
  //       ) : (
  //         <Badge
  //           className={cn("cursor-pointer", className)}
  //           tabIndex={0}
  //           data-focusable
  //           // onFocus={() => setOpen(true)}
  //           // onBlur={() => setOpen(false)}
  //         >
  //           {accession.testType}
  //         </Badge>
  //       )}
  //     </HoverCardTrigger>
  //     <HoverCardContent className="p-0 z-50">
  //       <Text
  //         as="h3"
  //         size="2"
  //         bold
  //         className="mb-2 flex items-center justify-between"
  //       >
  //         Test orders
  //       </Text>
  //       <ul className="list-disc list-inside">
  //         {isLoading
  //           ? new Array(3).fill(0).map((_, i) => (
  //               <li key={i}>
  //                 <Skeleton className="h-4 w-20" />
  //               </li>
  //             ))
  //           : truncated.map((order) => (
  //               <li key={order.id} className="truncate">
  //                 <Text size="1">{order.testSpec.name}</Text>
  //               </li>
  //             ))}
  //         {moreText}
  //       </ul>
  //     </HoverCardContent>
  //   </HoverCard>
  // );
}
